import { useRef } from 'react';
import { NextPage } from 'next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import {
  ImpressionTracker,
  PageViewTracker,
  template,
  PATHS,
} from '@belong/common';
import { GoogleStars, Image, Text, Link } from '@belong/ui';
import type { PrismicDocument } from '@prismicio/types';
import clsx from 'clsx';
import { UnitListingModel, ReviewModel } from 'src/api/models';
import { type IntercomWidgetProps } from 'src/components/intercom-widget/intercom-widget';
import { MetaSEO } from 'src/components/meta-seo/meta-seo';
import { StepsSection } from 'src/components/steps-section/steps-section';
import { TestimonialReviews } from 'src/components/testimonial-reviews/testimonial-reviews';
import { TestimonialVideos } from 'src/components/testimonial-videos/testimonial-videos';
import { useRegions } from 'src/features/app';
import { BlogPostCarousel } from 'src/features/blog/components/blog-post-carousel/blog-post-carousel';
import { FaqSection } from 'src/features/home-page/components/FaqSection/FaqSection';
import type { CitiesLandingPageProps } from 'src/features/homeowners-city/components/cities-landing-page/cities-landing-page';
import { DemographicsSection } from 'src/features/homeowners-city/components/demographics-section/demographics-section';
import { GmbProfile } from 'src/features/homeowners-city/components/gmb-profile/gmb-profile';
import { NearbySchoolsSection } from 'src/features/homeowners-city/components/nearby-schools/nearby-schools';
import { RentalStatsSection } from 'src/features/homeowners-city/components/rental-stats-section/rental-stats-section';
import { SocialProof } from 'src/features/homeowners-city/components/social-proof/social-proof';
import { getGoogleMyBusinessProfile } from 'src/features/homeowners-city/utils';
import { NeighborhoodList } from 'src/features/homeowners-neighborhood/components/neighborhood-list/neighborhood-list';
import { type School } from 'src/features/homeowners-neighborhood/types';
import { CalculatorSectionVariant } from 'src/features/homeowners/components/calculator-section/calculator-section-variant';
import { PriceTablePreview } from 'src/features/homeowners/components/price-table-preview/price-table-preview';
import { ListingsCarousel } from 'src/features/listing-page/components/listings-carousel/listings-carousel';
import { useStore } from 'src/store';
import { regionSelector } from 'src/store/region';
import { NeighborhoodCover } from '../neighborhood-cover/neighborhood-cover';
import { stepsBlocks, REGION_ARTICLES_MAP } from './constants';
import { NEIGHBORHOOD_LANDING_PAGE_STRINGS } from './landing-page-strings';
import css from './landing-page.module.css';
import type { InitialZustandState } from 'src/store/region';

const IntercomWidget = dynamic<IntercomWidgetProps>(
  () =>
    import('src/components/intercom-widget/intercom-widget').then(
      (mod) => mod.IntercomWidget,
    ),
  { ssr: false },
);

export const NeighborhoodLandingPage: NextPage<
  NeighborhoodLandingPageProps
> = ({
  neighborhoodData,
  cityData,
  listings,
  blogPosts,
  coverSrc,
  reviews,
}) => {
  const priceTableRef = useRef<HTMLDivElement>(null);
  const region = useStore(regionSelector);
  const { regions } = useRegions();
  const router = useRouter();

  const { cityOrRegionSlug } = router.query;
  const { city, neighborhoods, stateId } = cityData;

  const {
    neighborhood,
    neighborhoodAdditional,
    population,
    married,
    medianHouseHoldIncome,
    homeOwnership,
    homeValue,
    walkScore,
    transitScore,
    schools,
    walkscoreLink,
  } = neighborhoodData;

  const { description } = neighborhoodAdditional || {};

  const neighborhoodName = neighborhood.replace(new RegExp(' ', 'g'), '\u00a0');

  const metaTitle = template(NEIGHBORHOOD_LANDING_PAGE_STRINGS['meta.title'], {
    neighborhoodName,
    cityName: city,
  });

  const metaDescription = template(
    NEIGHBORHOOD_LANDING_PAGE_STRINGS['meta.description'],
    {
      neighborhoodName,
      city,
    },
  );

  const cityRegion = regions.find(
    (region) => region.name === cityData.region.name,
  );

  const parsedCityOrRegionSlug = (cityOrRegionSlug as string).replace(
    '-property-management',
    '',
  );
  const gmbLink = getGoogleMyBusinessProfile({
    city: parsedCityOrRegionSlug,
    regionId: region.id,
  });

  // `?? ''` rather than indexing with `undefined`: an unknown region name and
  // a missing one both mean "no banner".
  const RegionBanner = REGION_ARTICLES_MAP[cityRegion?.name ?? ''];

  return (
    <PageViewTracker name="homeowners_neighborhood_landing_page">
      <IntercomWidget isEnabled />
      <MetaSEO
        title={metaTitle}
        description={metaDescription}
        openGraph={{
          title: metaTitle,
          description: metaDescription,
        }}
      />
      <div className={clsx('mb-3xl md:mb-6xl', css.cover)}>
        {/* `city` and `neighborhoodNameRaw` were passed here too;
            `NeighborhoodCover` declares neither and destructures neither, so
            they never reached it. */}
        <NeighborhoodCover
          src={coverSrc}
          neighborhoodName={neighborhoodName}
          phoneNumber={region?.information?.contactInformation?.phone}
          state={stateId}
        />
      </div>
      <div className="overflow-hidden">
        <div className="container flex flex-col items-center">
          <Text
            variant="h2"
            fontWeight="semibold"
            className={clsx(css.valuePropsTitle, 'mb-sm text-center')}
          >
            {NEIGHBORHOOD_LANDING_PAGE_STRINGS['valueProps.title']}
          </Text>
          <Text
            variant="h3"
            className={clsx(css.valuePropsTitle, 'mb-2xl text-center')}
          >
            {NEIGHBORHOOD_LANDING_PAGE_STRINGS['valueProps.subtitle']}
          </Text>
          <div className="mb-5xl xl:mb-6xl relative">
            <StepsSection data={stepsBlocks.section1} />
            <div className={clsx('absolute hidden md:block', css.redBalloon)}>
              <Image alt="" src="/homeowner-city-lp/city_neon_balloon_02.png" />
            </div>
          </div>
          {RegionBanner && (
            <article className="mb-5xl xl:mb-6xl w-full">
              <RegionBanner />
            </article>
          )}
          <div className="mb-5xl xl:mb-6xl relative w-full">
            <SocialProof regionName={region?.name} />
            <div className={clsx('absolute hidden md:block', css.blueBalloon)}>
              <Image alt="" src="/homeowner-city-lp/city_neon_balloon_01.png" />
            </div>
          </div>
          <div className="mb-3xl container">
            <Text
              variant="h2"
              fontWeight="semibold"
              className={clsx(
                'mb-xl lg:mb-sm mx-auto text-center',
                css.descriptionTitle,
              )}
            >
              {template(
                NEIGHBORHOOD_LANDING_PAGE_STRINGS['descriptionSection.title'],
                { neighborhoodName },
              )}
            </Text>
            {description && <Text className="text-center">{description}</Text>}
          </div>
          <div className="mb-3xl container w-full">
            <Text variant="h3" fontWeight="semibold" className="mb-sm lg:mb-xl">
              Demographics
            </Text>
            <DemographicsSection
              population={population}
              medianHouseholdIncome={medianHouseHoldIncome}
              percentMarried={married}
            />
          </div>
          <div
            className={clsx(
              'mb-3xl container w-full',
              !schools?.length && 'mb-5xl xl:mb-6xl',
            )}
          >
            <Text variant="h3" fontWeight="semibold" className="mb-sm lg:mb-xl">
              Rental Stats
            </Text>
            <RentalStatsSection
              percentRent={100 - homeOwnership}
              homeValue={homeValue}
              walkingScore={walkScore}
              transitScore={transitScore}
              walkscoreLink={walkscoreLink}
            />
          </div>
          {schools?.length > 0 && (
            <div className="mb-5xl xl:mb-6xl container w-full">
              <Text
                variant="h3"
                fontWeight="semibold"
                className="mb-sm lg:mb-xl"
              >
                Nearby Schools
              </Text>
              <NearbySchoolsSection schools={schools} />
            </div>
          )}
          <div className="mb-5xl xl:mb-6xl container w-full">
            <CalculatorSectionVariant />
          </div>
        </div>
        <ImpressionTracker section="testimonial_carousel">
          <div className="mb-5xl xl:mb-6xl flex flex-col">
            <div className="container">
              <div className="mb-sm">
                <Text
                  variant="h2"
                  fontWeight="semibold"
                  className={clsx(
                    'mx-auto text-center md:max-w-none',
                    css.reviewsTitle,
                  )}
                >
                  {NEIGHBORHOOD_LANDING_PAGE_STRINGS['testimonials.title']}
                </Text>
              </div>
              <div className="mb-xl">
                <Text
                  variant="h3"
                  className={clsx(
                    'mx-auto text-center md:max-w-none',
                    css.reviewsSubtitle,
                  )}
                >
                  {template(
                    NEIGHBORHOOD_LANDING_PAGE_STRINGS['testimonials.text'],
                    { cityName: city },
                  )}
                </Text>
              </div>
              <div className="mb-2xl flex justify-center">
                <GoogleStars />
              </div>
            </div>
            <div className="carousel-container mb-5xl xl:mb-6xl">
              <TestimonialReviews reviews={reviews} />
            </div>
            <div className="container">
              <Text
                variant="h2"
                fontWeight="semibold"
                className={clsx(
                  'mx-auto text-center lg:max-w-none',
                  css.videoTestimonialsTitle,
                )}
              >
                {NEIGHBORHOOD_LANDING_PAGE_STRINGS['video.testimonials.title']}
              </Text>
            </div>
            <div className="carousel-container mt-xl md:mt-2xl mx-auto flex justify-center">
              <TestimonialVideos />
            </div>
          </div>
        </ImpressionTracker>
        <div className="container flex flex-col items-center">
          <div className="mb-5xl xl:mb-6xl container w-full">
            <PriceTablePreview
              title={NEIGHBORHOOD_LANDING_PAGE_STRINGS['priceTable.title']}
              subtitle={
                NEIGHBORHOOD_LANDING_PAGE_STRINGS['priceTable.subtitle']
              }
              regionId={cityRegion?.id}
              showMore={
                NEIGHBORHOOD_LANDING_PAGE_STRINGS['priceTable.showMore']
              }
              showLess={
                NEIGHBORHOOD_LANDING_PAGE_STRINGS['priceTable.showLess']
              }
              priceTableRef={priceTableRef}
            />
          </div>
        </div>
        {listings.length > 0 && (
          <div className="mb-5xl xl:mb-6xl w-full">
            <div className="container md:contents">
              <Text variant="h2" className="text-center" fontWeight="semibold">
                {NEIGHBORHOOD_LANDING_PAGE_STRINGS['listings.title'](
                  neighborhoodName,
                )}
              </Text>
              <div className="mt-2xl">
                <ListingsCarousel units={listings} />
              </div>
            </div>
          </div>
        )}
        <div className="mb-5xl xl:mb-6xl w-full">
          <div className="container md:contents">
            <div className="mb-sm">
              <Text variant="h2" className="text-center" fontWeight="semibold">
                {NEIGHBORHOOD_LANDING_PAGE_STRINGS['blog.title']}
              </Text>
            </div>
            <div className="mb-2xl">
              <Text variant="h3" className="text-center md:max-w-none">
                {template(NEIGHBORHOOD_LANDING_PAGE_STRINGS['blog.subtitle'], {
                  neighborhoodName,
                })}
              </Text>
            </div>
            <div className="mb-xl">
              <BlogPostCarousel blogPostList={blogPosts} />
            </div>
            <div className={clsx('m-auto', css.viewAllArticles)}>
              <Link
                href={PATHS.BLOG}
                buttonType="secondary"
                variant="outline"
                size="fluid"
              >
                {NEIGHBORHOOD_LANDING_PAGE_STRINGS['blog.link']}
              </Link>
            </div>
          </div>
        </div>
        <div className="container flex flex-col items-center">
          {gmbLink && (
            <div className="mb-5xl xl:mb-6xl container w-full">
              <GmbProfile gmbLink={gmbLink} />
            </div>
          )}
          <div className="mb-5xl xl:mb-6xl container w-full">
            <FaqSection />
          </div>
          {neighborhoods.length > 0 && (
            <div className="mb-5xl xl:mb-6xl container w-full">
              <Text variant="h2" className="text-center" fontWeight="semibold">
                <span className="hidden sm:block">
                  {template(
                    NEIGHBORHOOD_LANDING_PAGE_STRINGS['neighborhoods.title'],
                    { cityName: city },
                  )}
                </span>
                <span className="block sm:hidden">
                  {template(
                    NEIGHBORHOOD_LANDING_PAGE_STRINGS['neighborhoods.title'],
                    {
                      regionName: cityRegion?.region || city,
                    },
                  )}
                </span>
              </Text>
              <Text variant="h3" className="mb-xl mt-sm text-center">
                {NEIGHBORHOOD_LANDING_PAGE_STRINGS['neighborhoods.text']}
              </Text>
              <NeighborhoodList neighborhoods={neighborhoods} />
            </div>
          )}
        </div>
      </div>
    </PageViewTracker>
  );
};

export type NeighborhoodLandingPageProps = {
  neighborhoodData: {
    neighborhood: string;
    city_link?: string;
    neighborhoodAdditional: { description: string };
    population: number;
    medianHouseHoldIncome: number;
    married: number;
    homeOwnership: number;
    homeValue: number;
    walkScore: number;
    walkscoreLink: string;
    transitScore: number;
    schools: School[];
  };
  initialZustandState?: InitialZustandState;
  cityData: CitiesLandingPageProps['cityData'];
  listings: UnitListingModel[];
  blogPosts: PrismicDocument[];
  coverSrc: string;
  reviews: ReviewModel[];
  slug: string;
};
